import { useState, useMemo } from 'react'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import {
  useEventBudget,
  useBudgetCategories,
  useCreateBudgetLine,
  useUpdateBudgetLine,
  useDeleteBudgetLine,
  useBaselineBudget,
  useRebaselineBudget,
  useEventMeracRollup
} from '../hooks/useBudget'
import { Icon } from './Icon'
import { formatGBP } from '../lib/money'
import type { BudgetLine, BudgetLineBody, BudgetCategory, MeracRollupPayload } from '../types'

// BudgetTab - per-event budget tracker. Visible to financial users only; the
// server returns 403 to others and the tab strip hides the tab for them
// (see EventDetail.tsx).
//
// Layout: headline strip (KPI cards) + baseline banner + costs section +
// revenue section. Each section is grouped by category; rows have inline
// amount edits and sub-total per category.

interface Props {
  eventId: number
}

export function BudgetTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: me } = useMe()
  const { data, isLoading, error } = useEventBudget(eventId)
  const { data: categories } = useBudgetCategories()
  const { data: meracRollup, isLoading: meracLoading } = useEventMeracRollup(eventId)
  const createMut = useCreateBudgetLine(eventId)
  const updateMut = useUpdateBudgetLine(eventId)
  const deleteMut = useDeleteBudgetLine(eventId)
  const baselineMut = useBaselineBudget(eventId)
  const rebaselineMut = useRebaselineBudget(eventId)

  const [creatingInCategory, setCreatingInCategory] = useState<BudgetCategory | null>(null)
  const [editingLineId, setEditingLineId] = useState<number | null>(null)

  // Hard-stop for non-financial users. The server already returns 403; we
  // render a friendly locked-state so the URL is still navigable.
  if (me && !me.hasFinancial) {
    return (
      <div className="bg-white border border-line rounded p-10 text-center">
        <div
          className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
          style={{ background: '#ece6db', color: '#5a5a5a' }}
        >
          🔒
        </div>
        <h3 className="font-display text-lg mb-1">Financial access required</h3>
        <p className="text-sm text-ink-soft max-w-md mx-auto">
          You don't have financial access to this event. Speak to your event
          manager or admin to be added to the Finance role.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading budget…</div>
  }
  if (error) {
    if (error instanceof Error && error.message.startsWith('403')) {
      return (
        <div className="bg-white border border-line rounded p-10 text-center text-sm text-ink-soft">
          Financial access required.
        </div>
      )
    }
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load budget</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }
  if (!data) return null

  const { lines, totals, baselined, baselinedAt, baselinedByName } = data
  const costCategories = (categories ?? []).filter((c) => c.kind === 'cost')
  const revenueCategories = (categories ?? []).filter((c) => c.kind === 'revenue')

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl">Budget</h2>
        <p className="text-sm text-ink-soft mt-0.5">
          Planned vs committed vs actual. Baseline once the plan is locked
          to track variance.
        </p>
      </div>

      {/* Headline strip */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <KpiCard label="Total cost" value={totals.cost.actual} planned={totals.cost.planned} accent="brass" />
        <KpiCard label="Total revenue" value={totals.revenue.actual} planned={totals.revenue.planned} accent="green" />
        <KpiCard label="Net" value={totals.net} accent={totals.net >= 0 ? 'green' : 'oxblood'} />
        <KpiCard
          label="Variance vs baseline"
          value={null}
          variancePct={totals.netVariancePct}
          accent={varianceAccent(totals.netVariancePct)}
        />
      </div>

      {/* Baseline banner */}
      {!baselined && lines.length > 0 && (
        <div className="rounded p-4 flex items-center gap-3" style={{ background: '#f5edd5', color: '#9b7f2a' }}>
          <Icon name="alert-triangle" size={16} />
          <div className="flex-1">
            <div className="font-semibold text-sm">Plan not yet baselined</div>
            <div className="text-xs">
              Baseline the budget once the plan is locked so variance is
              calculated against the agreed numbers.
            </div>
          </div>
          {!archived && (
            <button
              type="button"
              onClick={() => baselineMut.mutate()}
              disabled={baselineMut.isPending}
              className="px-3 py-1.5 rounded text-xs font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
            >
              {baselineMut.isPending ? 'Baselining…' : 'Baseline now'}
            </button>
          )}
        </div>
      )}
      {baselined && (
        <div className="rounded p-3 flex items-center gap-3 text-xs" style={{ background: '#dcebe5', color: '#0a4a3a' }}>
          <Icon name="check-circle" size={14} />
          <span className="flex-1">
            Baselined {baselinedAt ? new Date(baselinedAt).toLocaleDateString('en-GB') : ''}
            {baselinedByName ? ` by ${baselinedByName}` : ''}.
          </span>
          {me?.isAdmin && !archived && (
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm('Re-baseline the budget? This snapshots the current planned amounts again and is admin-only.')
                if (!ok) return
                rebaselineMut.mutate()
              }}
              disabled={rebaselineMut.isPending}
              className="text-xs font-semibold underline hover:no-underline disabled:opacity-50"
            >
              {rebaselineMut.isPending ? 'Re-baselining…' : 'Rebaseline'}
            </button>
          )}
        </div>
      )}

      {/* MERAC actuals — live ticket-revenue sums from MeracSessions
          joined via MeracEventMap. Read-only, no in-app edits — the
          source of truth is the ontrack .ps1 → meracSync pipe. */}
      <MeracActualsSection rollup={meracRollup} isLoading={meracLoading} />

      {/* Costs */}
      <Section
        title="Costs"
        categories={costCategories}
        lines={lines.filter((l) => l.category.kind === 'cost')}
        baselined={baselined}
        archived={archived}
        editingLineId={editingLineId}
        setEditingLineId={setEditingLineId}
        onAdd={(cat) => setCreatingInCategory(cat)}
        onUpdate={(line, body) => updateMut.mutate({ lineId: line.id, body })}
        onDelete={(line) => {
          const ok = window.confirm(`Delete "${line.description}"?`)
          if (ok) deleteMut.mutate(line.id)
        }}
      />

      {/* Revenue */}
      <Section
        title="Revenue"
        categories={revenueCategories}
        lines={lines.filter((l) => l.category.kind === 'revenue')}
        baselined={baselined}
        archived={archived}
        editingLineId={editingLineId}
        setEditingLineId={setEditingLineId}
        onAdd={(cat) => setCreatingInCategory(cat)}
        onUpdate={(line, body) => updateMut.mutate({ lineId: line.id, body })}
        onDelete={(line) => {
          const ok = window.confirm(`Delete "${line.description}"?`)
          if (ok) deleteMut.mutate(line.id)
        }}
      />

      {!archived && creatingInCategory && (
        <NewLineModal
          category={creatingInCategory}
          saving={createMut.isPending}
          serverError={createMut.error as Error | null}
          onClose={() => setCreatingInCategory(null)}
          onSave={(body) => {
            createMut.mutate(
              { ...body, categoryId: creatingInCategory.id },
              { onSuccess: () => setCreatingInCategory(null) }
            )
          }}
        />
      )}
    </div>
  )
}

// ---- KPI card -------------------------------------------------------------

function KpiCard({
  label, value, planned, variancePct, accent
}: {
  label: string
  value: number | null
  planned?: number
  variancePct?: number | null
  accent: 'green' | 'brass' | 'oxblood' | 'slate'
}) {
  const ACCENTS = { green: '#0a4a3a', brass: '#9b7f2a', oxblood: '#7a2329', slate: '#3d5a6c' }
  const valueDisplay =
    value != null
      ? fmtMoney(value)
      : variancePct == null
      ? '—'
      : `${variancePct >= 0 ? '+' : ''}${variancePct.toFixed(1)}%`
  return (
    <div
      className="relative bg-white border border-line rounded-lg overflow-hidden"
      style={{ padding: '18px 20px' }}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: ACCENTS[accent] }}
      />
      <div className="text-[11.5px] uppercase tracking-wider font-semibold text-ink-soft mb-1.5">
        {label}
      </div>
      <div className="font-display text-2xl font-bold leading-none">{valueDisplay}</div>
      {planned != null && (
        <div className="text-xs text-ink-soft mt-1">vs {fmtMoney(planned)} planned</div>
      )}
    </div>
  )
}

function varianceAccent(pct: number | null): 'green' | 'brass' | 'oxblood' | 'slate' {
  if (pct == null) return 'slate'
  if (pct <= 0) return 'green'
  if (pct <= 10) return 'brass'
  return 'oxblood'
}

// ---- MERAC actuals section ----------------------------------------------
//
// Renders the live ticket-revenue rollup from MeracSessions. Three states:
//   - loading              → muted placeholder
//   - sessionCount === 0   → empty state pointing the user at MeracEventMap
//   - data present         → gross / net / count / freshness stamp
//
// Money is BIGINT pence on the wire — render via formatGBP. net can be
// negative (refunds + adjustments); formatGBP renders that as "-£300".

function MeracActualsSection({
  rollup, isLoading
}: {
  rollup: MeracRollupPayload | undefined
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="bg-white border border-line rounded">
        <div className="px-4 py-3 border-b border-line">
          <h3 className="font-display text-lg">MERAC actuals</h3>
        </div>
        <div className="px-4 py-3 text-xs text-ink-soft font-mono">
          Loading ticket revenue…
        </div>
      </div>
    )
  }

  if (!rollup || rollup.sessionCount === 0) {
    return (
      <div className="bg-white border border-line rounded">
        <div className="px-4 py-3 border-b border-line">
          <h3 className="font-display text-lg">MERAC actuals</h3>
        </div>
        <div className="px-4 py-3 text-xs italic text-ink-soft">
          No MERAC sessions mapped yet — check MeracEventMap for this event.
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line flex items-baseline gap-3">
        <h3 className="font-display text-lg flex-1">MERAC actuals</h3>
        <span className="text-[11px] text-ink-soft font-mono">
          {formatMeracFreshness(rollup.lastSessionAt)}
        </span>
      </div>
      <div className="grid gap-4 p-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <MeracStat label="Gross revenue" value={formatGBP(rollup.grossRevenuePence)} accent="green" />
        <MeracStat label="Net revenue"   value={formatGBP(rollup.netRevenuePence)}   accent={rollup.netRevenuePence >= 0 ? 'green' : 'oxblood'} />
        <MeracStat label="Sessions"      value={String(rollup.sessionCount)}         accent="slate" />
      </div>
    </div>
  )
}

function MeracStat({
  label, value, accent
}: {
  label: string
  value: string
  accent: 'green' | 'brass' | 'oxblood' | 'slate'
}) {
  const ACCENTS = { green: '#0a4a3a', brass: '#9b7f2a', oxblood: '#7a2329', slate: '#3d5a6c' }
  return (
    <div className="relative bg-parchment/60 rounded" style={{ padding: '12px 14px' }}>
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0"
        style={{ width: 3, background: ACCENTS[accent] }}
      />
      <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-soft mb-1">
        {label}
      </div>
      <div className="font-display text-xl font-bold leading-none">{value}</div>
    </div>
  )
}

// "last session HH:mm on DD MMM" per the brief. Locale en-GB; if the
// timestamp is unparseable we drop the stamp rather than render "Invalid
// Date".
function formatMeracFreshness(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  return `last session ${time} on ${date}`
}

// ---- Section -------------------------------------------------------------

function Section({
  title, categories, lines, baselined, archived, editingLineId, setEditingLineId, onAdd, onUpdate, onDelete
}: {
  title: string
  categories: BudgetCategory[]
  lines: BudgetLine[]
  baselined: boolean
  archived: boolean
  editingLineId: number | null
  setEditingLineId: (id: number | null) => void
  onAdd: (cat: BudgetCategory) => void
  onUpdate: (line: BudgetLine, body: BudgetLineBody) => void
  onDelete: (line: BudgetLine) => void
}) {
  const byCategory = useMemo(() => {
    const m = new Map<number, BudgetLine[]>()
    for (const l of lines) {
      const arr = m.get(l.category.id) ?? []
      arr.push(l)
      m.set(l.category.id, arr)
    }
    return m
  }, [lines])

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line">
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div className="divide-y divide-line">
        {categories.map((cat) => {
          const catLines = byCategory.get(cat.id) ?? []
          const subPlanned = catLines.reduce((s, l) => s + l.plannedAmount, 0)
          const subActual = catLines.reduce((s, l) => s + l.actualAmount, 0)
          return (
            <div key={cat.id}>
              <div className="px-4 py-2 flex items-center gap-3 bg-parchment">
                <span className="text-xs font-semibold uppercase tracking-wide flex-1">
                  {cat.name}
                </span>
                <span className="text-xs text-ink-soft font-mono">
                  {fmtMoney(subActual)} / {fmtMoney(subPlanned)}
                </span>
                {!archived && (
                  <button
                    type="button"
                    onClick={() => onAdd(cat)}
                    className="text-xs font-semibold text-green hover:underline"
                  >
                    + Add line
                  </button>
                )}
              </div>
              {catLines.length === 0 ? (
                <div className="px-4 py-3 text-xs italic text-ink-soft">
                  No {cat.name.toLowerCase()} lines yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line">
                      <th className="px-4 py-1.5">Description</th>
                      <th className="px-3 py-1.5 text-right w-24">Planned</th>
                      <th className="px-3 py-1.5 text-right w-24">Committed</th>
                      <th className="px-3 py-1.5 text-right w-24">Actual</th>
                      <th className="px-3 py-1.5 text-right w-20">Variance</th>
                      <th className="px-3 py-1.5 w-16" />
                    </tr>
                  </thead>
                  <tbody>
                    {catLines.map((line) => (
                      <LineRow
                        key={line.id}
                        line={line}
                        isEditing={editingLineId === line.id}
                        baselined={baselined}
                        archived={archived}
                        onStartEdit={() => setEditingLineId(line.id)}
                        onCancelEdit={() => setEditingLineId(null)}
                        onSave={(body) => {
                          onUpdate(line, body)
                          setEditingLineId(null)
                        }}
                        onDelete={() => onDelete(line)}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---- Line row -------------------------------------------------------------

function LineRow({
  line, isEditing, baselined, archived, onStartEdit, onCancelEdit, onSave, onDelete
}: {
  line: BudgetLine
  isEditing: boolean
  baselined: boolean
  archived: boolean
  onStartEdit: () => void
  onCancelEdit: () => void
  onSave: (body: BudgetLineBody) => void
  onDelete: () => void
}) {
  const [description, setDescription] = useState(line.description)
  const [planned, setPlanned] = useState(line.plannedAmount)
  const [committed, setCommitted] = useState(line.committedAmount)
  const [actual, setActual] = useState(line.actualAmount)

  const variancePct = baselineVariancePct(line, baselined)

  if (isEditing && !archived) {
    return (
      <tr className="border-b border-line last:border-b-0 bg-parchment/40">
        <td className="px-4 py-2">
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-line rounded px-2 py-1 text-sm"
          />
        </td>
        <td className="px-3 py-2 text-right">
          <input
            type="number"
            step="0.01"
            value={planned}
            onChange={(e) => setPlanned(Number(e.target.value))}
            className="w-24 border border-line rounded px-2 py-1 text-sm text-right font-mono"
          />
        </td>
        <td className="px-3 py-2 text-right">
          <input
            type="number"
            step="0.01"
            value={committed}
            onChange={(e) => setCommitted(Number(e.target.value))}
            className="w-24 border border-line rounded px-2 py-1 text-sm text-right font-mono"
          />
        </td>
        <td className="px-3 py-2 text-right">
          <input
            type="number"
            step="0.01"
            value={actual}
            onChange={(e) => setActual(Number(e.target.value))}
            className="w-24 border border-line rounded px-2 py-1 text-sm text-right font-mono"
          />
        </td>
        <td className="px-3 py-2 text-right text-xs text-ink-soft">—</td>
        <td className="px-3 py-2">
          <div className="flex gap-1 items-center">
            <button
              type="button"
              onClick={() =>
                onSave({
                  categoryId: line.category.id,
                  description: description.trim() || line.description,
                  plannedAmount: planned,
                  committedAmount: committed,
                  actualAmount: actual,
                  linkedComponentId: line.linkedComponent?.id ?? null,
                  notes: line.notes,
                  sortOrder: line.sortOrder
                })
              }
              className="text-xs font-semibold text-green hover:underline"
            >
              Save
            </button>
            <button
              type="button"
              onClick={onCancelEdit}
              className="text-xs text-ink-soft hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-parchment transition-colors">
      <td className="px-4 py-2">
        {archived ? (
          <span className="font-semibold">{line.description}</span>
        ) : (
          <button type="button" onClick={onStartEdit} className="text-left font-semibold hover:text-green">
            {line.description}
          </button>
        )}
        {line.linkedComponent && (
          <div className="text-[10px] text-ink-soft mt-0.5">
            Linked to component: {line.linkedComponent.name}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono">{fmtMoney(line.plannedAmount)}</td>
      <td className="px-3 py-2 text-right font-mono">{fmtMoney(line.committedAmount)}</td>
      <td className="px-3 py-2 text-right font-mono">{fmtMoney(line.actualAmount)}</td>
      <td className="px-3 py-2 text-right">
        {variancePct == null ? (
          <span className="text-xs text-ink-soft">—</span>
        ) : (
          <span
            className="text-[11px] font-mono px-1.5 py-0.5 rounded"
            style={{
              background: varianceColours(variancePct).bg,
              color: varianceColours(variancePct).fg
            }}
          >
            {variancePct >= 0 ? '+' : ''}{variancePct.toFixed(1)}%
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {!archived && (
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete line"
            className="text-ink-soft hover:text-oxblood text-xs"
          >
            ×
          </button>
        )}
      </td>
    </tr>
  )
}

function baselineVariancePct(line: BudgetLine, baselined: boolean): number | null {
  const ref = baselined ? line.baselinePlannedAmount ?? line.plannedAmount : line.plannedAmount
  if (!ref) return null
  return ((line.actualAmount - ref) / Math.abs(ref)) * 100
}

function varianceColours(pct: number): { bg: string; fg: string } {
  if (pct <= 0) return { bg: '#dcebe5', fg: '#0a4a3a' }
  if (pct <= 10) return { bg: '#f5edd5', fg: '#9b7f2a' }
  return { bg: '#f8dbdd', fg: '#7a2329' }
}

// ---- New line modal -------------------------------------------------------

function NewLineModal({
  category, saving, serverError, onClose, onSave
}: {
  category: BudgetCategory
  saving: boolean
  serverError: Error | null
  onClose: () => void
  onSave: (body: Omit<BudgetLineBody, 'categoryId'>) => void
}) {
  const [description, setDescription] = useState('')
  const [planned, setPlanned] = useState(0)
  const [committed, setCommitted] = useState(0)
  const [actual, setActual] = useState(0)
  const [notes, setNotes] = useState('')

  const isValid = description.trim().length > 0
  function submit() {
    if (!isValid) return
    onSave({
      description: description.trim(),
      plannedAmount: planned,
      committedAmount: committed,
      actualAmount: actual,
      linkedComponentId: null,
      notes: notes.trim() || null,
      sortOrder: 9999
    })
  }

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center p-5"
      style={{ background: 'rgba(31, 29, 24, 0.55)', backdropFilter: 'blur(4px)' }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="bg-white rounded-lg shadow-xl w-full max-w-xl my-8 p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">New {category.kind} line · {category.name}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink text-xl">×</button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
              Description
            </span>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={200}
              className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
            />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <NumberField label="Planned (£)" value={planned} onChange={setPlanned} />
            <NumberField label="Committed (£)" value={committed} onChange={setCommitted} />
            <NumberField label="Actual (£)" value={actual} onChange={setActual} />
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
              Notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
            />
          </label>
          {serverError && (
            <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
              {serverError.message}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-parchment"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || saving}
            className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add line'}
          </button>
        </div>
      </form>
    </div>
  )
}

function NumberField({
  label, value, onChange
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">
        {label}
      </span>
      <input
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full border border-line rounded px-2 py-1.5 text-sm text-right font-mono focus:outline-none focus:border-green"
      />
    </label>
  )
}

// ---- Helpers --------------------------------------------------------------

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(n)
}
